import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksDropdown } from '../../../components/liks/LiksDropdown/LiksDropdown';
import { LiksSearch } from '../../../components/liks/LiksSearch/LiksSearch';
import { useCreateLacUserMutation } from '../../../features/lac/mutations/useLacUserMutations';
import { useLacTenantOverviewQuery } from '../../../features/lac/queries/useLacTenantOverviewsQuery';
import { useLacUsersByTenantQuery } from '../../../features/lac/queries/useLacUsersByTenantQuery';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import type { LacCreateUserInput, LacUser } from '../../../types/lac';
import { toast } from '../../../toast/toast';
import { CreateLacUserDialog } from '../CreateLacUserDialog';
import { LacUserRow } from '../LacUserRow';
import { resolveLacTenantPath } from '../../../routes/lacPaths';
import { resolveUserRef } from '../../../features/user/userDisplay';
import './LacUsersTab.css';

type StatusFilter = 'all' | 'enabled' | 'disabled';

type LacTenantMembersTabProps = {
  tenantName: string;
  tenantDisplayName: string;
  maxUsers?: number | null;
};

function matchesStatusFilter(
  user: LacUser,
  statusFilter: StatusFilter,
): boolean {
  if (statusFilter === 'all') {
    return true;
  }
  if (user.enabled == null) {
    return false;
  }
  return statusFilter === 'enabled' ? user.enabled : !user.enabled;
}

function userListKey(user: LacUser): string {
  return resolveUserRef(user);
}

function resolveUserKnowledgeBaseCount(
  user: LacUser,
  metricsByUserKey: Map<string, number>,
): number | undefined {
  // Metrics `userId` is the person (Keycloak UUID). Match against User.id /
  // username only — User.userId is the audit creator.
  const candidates = [user.id?.trim(), user.name.trim()].filter(
    (value): value is string => Boolean(value),
  );

  for (const candidate of candidates) {
    const count = metricsByUserKey.get(candidate);
    if (count != null) {
      return count;
    }
  }

  return undefined;
}

export function LacTenantMembersTab({
  tenantName,
  tenantDisplayName,
  maxUsers = null,
}: LacTenantMembersTabProps) {
  const { translate } = useI18n();
  const usersByTenantQuery = useLacUsersByTenantQuery();
  // All-time overview (no date range) — KDB membership counts are not period-scoped.
  const overviewQuery = useLacTenantOverviewQuery(tenantName);
  const overview = overviewQuery.data;
  const createUserMutation = useCreateLacUserMutation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const tenantGroup = useMemo(
    () =>
      (usersByTenantQuery.data ?? []).find(
        (group) => group.tenantName === tenantName,
      ) ?? null,
    [usersByTenantQuery.data, tenantName],
  );

  const metricsByUserKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const metric of overview?.userKnowledgeBaseMetrics ?? []) {
      const count = metric.knowledgeBaseCount ?? 0;
      const userId = metric.userId?.trim();
      const username = metric.username?.trim();
      if (userId) {
        map.set(userId, count);
      }
      if (username) {
        map.set(username, count);
      }
    }
    return map;
  }, [overview?.userKnowledgeBaseMetrics]);

  const filteredUsers = useMemo(() => {
    const users = tenantGroup?.users ?? [];
    const query = searchQuery.trim().toLowerCase();

    return users.filter((user) => {
      if (!matchesStatusFilter(user, statusFilter)) {
        return false;
      }
      if (query === '') {
        return true;
      }
      return (
        user.name.toLowerCase().includes(query) ||
        (user.firstName?.toLowerCase().includes(query) ?? false) ||
        (user.lastName?.toLowerCase().includes(query) ?? false) ||
        (user.email?.toLowerCase().includes(query) ?? false)
      );
    });
  }, [tenantGroup?.users, searchQuery, statusFilter]);

  const userCount = tenantGroup?.totalUserCount ?? overview?.userCount ?? 0;
  const resolvedMaxUsers =
    maxUsers ?? tenantGroup?.maxUsers ?? overview?.maxUsers;
  const isTenantFull =
    resolvedMaxUsers != null && userCount >= resolvedMaxUsers;

  const handleStatusFilterChange = (value: string) => {
    if (value === 'all' || value === 'enabled' || value === 'disabled') {
      setStatusFilter(value);
    }
  };

  const handleCreateUser = (input: LacCreateUserInput) => {
    createUserMutation.mutate(input, {
      onSuccess: (data) => {
        if (data.roleAssigned) {
          toast.success({
            title: translate('lac.users.create.successTitle'),
            description: translate('lac.users.create.successDescription', {
              username: input.username,
            }),
          });
        }
        setIsCreateDialogOpen(false);
        if (!data.roleAssigned) {
          toast.error({
            title: translate('lac.users.create.roleErrorTitle'),
            description: translate('lac.users.create.roleErrorDescription', {
              username: input.username,
            }),
          });
        }
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('lac.users.create.errorTitle'),
          translate('lac.users.create.errorDescription'),
        );
      },
    });
  };

  if (usersByTenantQuery.isLoading) {
    return (
      <p className="lac-users__status">{translate('lac.users.loading')}</p>
    );
  }

  if (usersByTenantQuery.isError) {
    return (
      <p className="lac-users__status" role="alert">
        {translate('lac.users.loadError')}
      </p>
    );
  }

  const hasUsers = (tenantGroup?.users.length ?? 0) > 0;

  return (
    <>
      <div className="lac-users lac-users--tenant-members">
        <div className="lac-users__toolbar">
          <LiksSearch
            className="lac-users__search"
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={translate(
              'lac.organisations.members.searchPlaceholder',
            )}
            aria-label={translate('lac.users.searchLabel')}
            clearLabel={translate('common.search.clear')}
            variant="on-light"
          />
          <LiksDropdown
            className="lac-users__status-filter"
            variant="on-light"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            options={[
              {
                value: 'all',
                label: translate('lac.users.statusFilter.all'),
              },
              {
                value: 'enabled',
                label: translate('lac.users.enabled'),
              },
              {
                value: 'disabled',
                label: translate('lac.users.disabled'),
              },
            ]}
            aria-label={translate('lac.users.statusFilter.label')}
          />
          <LiksButton
            className="lac-users__create-button"
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={isTenantFull}
          >
            <Plus strokeWidth={2} aria-hidden />
            {translate('lac.users.create.open')}
          </LiksButton>
          <span
            className={[
              'lac-users__group-meta',
              'lac-users__members-count',
              isTenantFull && 'lac-users__group-meta--danger',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {resolvedMaxUsers != null
              ? translate('lac.users.userCountWithMax', {
                  count: String(userCount),
                  max: String(resolvedMaxUsers),
                })
              : translate('lac.users.userCount', {
                  count: String(userCount),
                })}
          </span>
        </div>

        {!hasUsers ? (
          <p className="lac-users__empty">
            {translate('lac.users.tenantEmpty')}
          </p>
        ) : filteredUsers.length === 0 ? (
          <p className="lac-users__empty">
            {translate('lac.users.noSearchResults')}
          </p>
        ) : (
          <ul
            className="lac-users__list lac-users__users liks-scrollbar"
            aria-label={translate('lac.organisations.tabs.users')}
          >
            {filteredUsers.map((user) => (
              <LacUserRow
                key={userListKey(user)}
                user={user}
                tenantName={tenantName}
                knowledgeBaseCount={resolveUserKnowledgeBaseCount(
                  user,
                  metricsByUserKey,
                )}
                profileLinkState={{
                  backTo: resolveLacTenantPath(tenantName, 'users'),
                }}
              />
            ))}
          </ul>
        )}
      </div>

      {isCreateDialogOpen && (
        <CreateLacUserDialog
          fixedTenantName={tenantName}
          fixedTenantDisplayName={tenantDisplayName}
          onCreate={handleCreateUser}
          onClose={() => setIsCreateDialogOpen(false)}
          isSubmitting={createUserMutation.isPending}
        />
      )}
    </>
  );
}
